import {
  ArrowDownIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  IconButton,
  Input,
  Option,
  Select,
  Tooltip,
  Typography,
} from "@material-tailwind/react";
import React, { useEffect, useState } from "react";
import { useAuthUserToken } from "../../../hooks/useAuthUserToken";
import { getAllClientsData } from "./../../../helperFunctions/apiFunction";
import TableSkeleton from "../../../components/Loaders/TableSkeleton";
import { Link, useNavigate } from "react-router-dom";
import { PencilIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";

const PlatformClient = () => {
  const navigate = useNavigate();
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [type, setType] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const TABLE_HEAD = [
    "Legal Name",
    "Contact Email",
    "Contact Phone Number",
    "Contact Mobile Number",
    "Action",
  ];

  useEffect(() => {
    setIsLoading(null);
    getAllClientsData(refreshToken, page, limit, sortBy, searchBy).then(
      (resp) => {
        if (resp?.code === 440)
          toast.error(resp?.message, {
            className: "text-[12px]",
          });
        setTemplateData(resp?.data?.results);
        setTotalPage(resp?.data?.totalPages);
        setIsLoading(false);
      }
    );
  }, [page, limit, sortBy, searchBy]);

  const handleEditClick = (clientId) => {
    navigate(`/client-management/editClient/${clientId}`);
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Clients Data");
    const headers = [
      "S.No.",
      "Legal Name",
      "Contact Email",
      "Contact Phone Number",
      "Contact Mobile Number",
    ];
    worksheet.addRow(headers);

    templateData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.clientLegalName,
        row.clientPrimaryContactEmail,
        row.clientPrimaryContactPhoneNumber,
        row.clientPrimaryContactMobileNumber,
      ]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Clients
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all details of all clients
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
              {rbac?.clientAdd ? (
                <Link
                  to={`/client-management/createNewClient`}
                  className="m-0 p-0 hover:text-white hover:no-underline decoration-0"
                >
                  <Button
                    className="flex items-center gap-3 no-underline cursor-pointer "
                    size="sm"
                    loading={isLoading === null ? true : false}
                  >
                    <UserPlusIcon strokeWidth={2} className="h-4 w-4" />
                    <span className="hidden md:inline-block"> Create New</span>
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                className="!border-t-0 !border-[#b0bec5]"
                label="Select Limit"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                className="!border-t-0 !border-[#b0bec5]"
                label="Sort By"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
              <Select
                color="yellow"
                className="!border-t-0 !border-[#b0bec5]"
                label="Type"
                value={type}
                onChange={(event) => {
                  setType(event);
                  setIsLoading(null);
                }}
              >
                <Option value="">All Data</Option>
                <Option value="users">User</Option>
                <Option value="policies">Policy</Option>
                <Option value="groups">Group</Option>
                <Option value="roles">Role</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-32"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}
                      {index !== TABLE_HEAD.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={TABLE_HEAD.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map(
                      (
                        {
                          clientId,
                          clientPrimaryContactMobileNumber,
                          clientPrimaryContactPhoneNumber,
                          clientPrimaryContactEmail,
                          clientLegalName,
                          id,
                        },
                        index
                      ) => {
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-3"
                          : "p-3 border-b border-blue-gray-50";

                        return (
                          <tr key={`${id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {clientLegalName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal capitalize"
                              >
                                {clientPrimaryContactEmail}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {clientPrimaryContactPhoneNumber}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {clientPrimaryContactMobileNumber}
                              </Typography>
                            </td>
                            {rbac?.clientEdit ? (
                              <>
                                <td className={classes}>
                                  <Tooltip
                                    className="bg-blue-gray-500"
                                    content="Edit"
                                  >
                                    <IconButton
                                      variant="text"
                                      onClick={() => handleEditClick(clientId)}
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page - 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setIsLoading(null);
                      setPage(index + 1);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page + 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default PlatformClient;
